import './App.css';

import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { QueryClient, QueryClientProvider, QueryErrorResetBoundary } from "@tanstack/react-query";
import { persistQueryClient } from "@tanstack/react-query-persist-client";
import { Amplify } from 'aws-amplify';
import { FC, useEffect, useMemo } from 'react';
import { ErrorBoundary } from "react-error-boundary";
import { Navigate, Outlet, Route, RouterProvider, createBrowserRouter, createRoutesFromElements } from "react-router-dom";
import 'semantic-ui-css/semantic.min.css';
import { accountId } from "./accounts";
import { Logout, ProvideUser, RequireUser } from "./auth/auth";
import awsExports from './aws-exports';
import { ProvideParams } from "./hooks";
import { AddAccount } from './views/AddAccount';
import { Dashboard, scanIndex } from "./views/ScanHistory";
import { Products } from './views/Products';
import { Checkout } from './views/Checkout';
import { CheckoutResult } from './views/CheckoutResults';
import { NavBar } from './views/NavBar';
import { Profile } from './views/Profile';
import { Home } from './views/Home';

Amplify.configure(awsExports);

const App: FC = () => {
  const queryClient = useMemo(() => new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 5, // 5 minutes
        cacheTime: 1000 * 60 * 60 * 24, // 24 hours
      },
    },
  }), [])
  useEffect(() => {
    const localStoragePersister = createSyncStoragePersister({storage: window.localStorage})
    const [unsubscribe] = persistQueryClient({
      queryClient,
      persister: localStoragePersister,
    })
    return unsubscribe
  }, [queryClient])

  return <QueryClientProvider client={queryClient}>
    <ProvideUser>
      <RouterProvider router={createBrowserRouter(createRoutesFromElements(routes()))}/>
    </ProvideUser>
  </QueryClientProvider>
}

function routes() {
  return <Route element={<QueryErrorResetBoundary>
    {({reset}) => (
      <ErrorBoundary
        onReset={reset}
        fallbackRender={({resetErrorBoundary}) => (
          <div>
            There was an error!
            <button onClick={() => resetErrorBoundary()}>Try again</button>
          </div>
        )}
      >
        <RequireUser>
          <Outlet/>
        </RequireUser>
      </ErrorBoundary>
    )}
  </QueryErrorResetBoundary>
  }>
    <Route path="accounts" element={<Dashboard/>}/>
    <Route path='logout' element={<Logout/>}/>
    <Route path='accounts/addNew' element={<AddAccount/>}/>
    <Route path='products' element={<Products/>}/>
    <Route path='profile' element={<Profile/>}/>
    <Route path="checkout" element={<ProvideParams params={{accountId}}/>}>
      <Route path='validate/:accountId/:result' element={<CheckoutResult/>}/>
      <Route path='pay/:productId' element={<Checkout/>}/>
      <Route path='pay/:accountId/:productId' element={<Checkout/>}/>
    </Route>
    <Route path="accounts/:accountId" element={<ProvideParams params={{accountId}}/>}>
      <Route path="scans" element={<Dashboard/>}/>
      <Route path="scans/:scanIndex" element={<ProvideParams params={{scanIndex}}>
        <Dashboard/>
      </ProvideParams>}/>
      <Route path="*" element={<Navigate to="scans"/>}/>
    </Route>
    <Route path="" element={<Home/>}/>
    <Route path="*" element={<Home/>}/>
  </Route>
}


export default App;
